import { am4core, am4charts } from "../loaders/amchartsLoader";
import store from "../store";
import { formatBytes, formatSizeUnits } from "../utils/format";
import { formatBitsSI } from "./format";
import { colors } from "./colors";
import { format, startOfHour } from "date-fns";
import { byteDecimalUnits } from "./enums";
import { getTimeInterval,
  downloadAsCSVFile 
} from "./common"; 
import { displayDateTime, shortDisplayDateTimeHour } from "./displayTime"; 
import { applyTooltipTheme, applyLegendTheme, createThemeChangeObserver } from "../common/Theme";
export const centerLegend = { align: "center" }; 

// Why this block is bigger than a normal scrollbar style helper:
// amCharts renders scrollbar grips as SVG sprites, which are visual-only in
// some keyboard/screen-reader flows. These helpers add hidden native range
// inputs over the visible grips, sync them with amCharts, and hide the SVG
// grips from assistive tech so JAWS announces one clear control per grip.
const SCROLLBAR_NATIVE_RANGE_SCALE = 1000;

// Keeps normalized amCharts scrollbar positions within the expected 0..1 range.
const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

// Resolves the DOM node behind an amCharts sprite across the sprite APIs we use.
const getScrollbarSpriteNode = sprite => sprite?.element?.node || sprite?.dom;

// Converts a normalized grip position into screen-reader-friendly date text.
const getScrollbarPositionText = (scrollBar, position) => {
  const date = scrollBar?.scrollbarChart?.xAxes?.getIndex?.(0)?.positionToDate?.(clamp(position, 0, 1));
  return date && Number.isFinite(date.getTime()) ? format(date, "MMMM d yyyy h mm a") : "Date unavailable";
};

// Applies DOM attributes defensively because some amCharts nodes are optional.
const setAttributes = (node, attributes) => {
  Object.entries(attributes).forEach(([attribute, value]) => {
    node?.setAttribute?.(attribute, value);
  });
};

// Prevents duplicate announcements from the original visual SVG scrollbar parts.
const hideScrollbarSpriteFromAssistiveTech = sprite => {
  if (!sprite) {
    return;
  }

  sprite.focusable = false;
  sprite.tabindex = -1;
  sprite.readerHidden = true;
  sprite.readerTitle = "";
  sprite.readerDescription = "";
  sprite.setSVGAttribute?.({
    "aria-hidden": "true",
    "focusable": "false",
    "tabindex": -1
  });
  const node = getScrollbarSpriteNode(sprite);
  setAttributes(node, {
    "aria-hidden": "true",
    "focusable": "false",
    "tabindex": -1
  });
};

// Builds the accessible name for each native grip control.
const getScrollbarControlLabel = (scrollBar, target) => {
  const defaultLabel = target === "end" ? "End date" : "Start date";
  const explicitLabel = target === "end"
    ? scrollBar?._nativeScrollbarEndLabel
    : scrollBar?._nativeScrollbarStartLabel;

  if (explicitLabel) {
    return explicitLabel;
  }

  return scrollBar?._nativeScrollbarLabelPrefix
    ? `${scrollBar._nativeScrollbarLabelPrefix} ${defaultLabel}`
    : defaultLabel;
};

// Refreshes accessible names when a chart passes a y-axis or chart-specific label.
const setScrollbarControlLabels = scrollBar => {
  scrollBar?._nativeScrollbarControls?.forEach(control => {
    control.input?.setAttribute("aria-label", getScrollbarControlLabel(scrollBar, control.target));
  });
};

// Updates amCharts range state exactly as a visual grip drag would.
const setScrollbarRange = (scrollBar, start, end, priority) => {
  const nextStart = clamp(start, 0, 1);
  const nextEnd = clamp(end, 0, 1);

  scrollBar._usingGrip = priority;
  scrollBar._skipRangeEvents = true;
  scrollBar._start = nextStart;
  scrollBar._end = nextEnd;
  scrollBar.updateThumb?.();
  scrollBar._skipRangeEvents = false;
  scrollBar.dispatchRangeChange?.();
  syncNativeScrollbarControls(scrollBar);
  positionNativeScrollbarControls(scrollBar);
  scrollBar._usingGrip = undefined;
};

// Keeps start/end controls from crossing or collapsing into an unusable range.
const getScrollbarMinRange = scrollBar => {
  return Math.max(0.001, Math.min(0.05, (scrollBar.thumb?.minWidth || 5) / (scrollBar.innerWidth || 1000)));
};

// Maps amCharts 0..1 positions to integer values for native range inputs.
const toNativeRangeValue = value => Math.round(clamp(value, 0, 1) * SCROLLBAR_NATIVE_RANGE_SCALE);

// Finds the DOM container where hidden native controls should be mounted.
const getNativeControlContainer = scrollBar => {
  return scrollBar?._nativeScrollbarControlContainerOverride ||
    scrollBar?.htmlContainer ||
    scrollBar?.parent?.htmlContainer ||
    scrollBar?.chart?.htmlContainer;
};

// Inserts controls before or after chart content to support required focus order.
const addNativeScrollbarControlElements = (container, controls, insertBeforeChart) => {
  const fragment = document.createDocumentFragment();
  controls.forEach(control => {
    fragment.appendChild(control.focusFrame);
    fragment.appendChild(control.input);
  });

  if (insertBeforeChart) {
    container.insertBefore(fragment, container.firstChild);
    return;
  }

  container.appendChild(fragment);
};

// Positions hidden native inputs over the visible amCharts start/end grips.
const positionNativeScrollbarControls = scrollBar => {
  const container = scrollBar?._nativeScrollbarControlContainer;
  if (!container || typeof window === "undefined") {
    return;
  }

  const containerRect = container.getBoundingClientRect();
  scrollBar._nativeScrollbarControls?.forEach(control => {
    const sprite = control.target === "end" ? scrollBar.endGrip : scrollBar.startGrip;
    const node = getScrollbarSpriteNode(sprite);
    if (!node?.getBoundingClientRect) {
      return;
    }

    const nodeRect = node.getBoundingClientRect();
    const width = Math.max(nodeRect.width, 8);
    const height = Math.max(nodeRect.height, 32);
    const rect = {
      height,
      left: nodeRect.left - containerRect.left - ((width - nodeRect.width) / 2),
      top: nodeRect.top - containerRect.top - ((height - nodeRect.height) / 2),
      width
    };
    [control.input, control.focusFrame].forEach(element => {
      Object.assign(element.style, {
        height: `${rect.height}px`,
        left: `${rect.left}px`,
        top: `${rect.top}px`,
        width: `${rect.width}px`
      });
    });
  });
};

// Calculates each native input's min/max so grips stay in a valid date window.
const getNativeControlBounds = (scrollBar, target) => {
  const minRange = getScrollbarMinRange(scrollBar);

  if (target === "end") {
    return {
      max: SCROLLBAR_NATIVE_RANGE_SCALE,
      min: toNativeRangeValue(scrollBar.start + minRange),
      value: toNativeRangeValue(scrollBar.end)
    };
  }

  return {
    max: toNativeRangeValue(scrollBar.end - minRange),
    min: 0,
    value: toNativeRangeValue(scrollBar.start)
  };
};

// Copies the current amCharts range into the native inputs and aria-valuetext.
const syncNativeScrollbarControls = scrollBar => {
  scrollBar?._nativeScrollbarControls?.forEach(control => {
    const bounds = getNativeControlBounds(scrollBar, control.target);
    control.input.min = bounds.min;
    control.input.max = Math.max(bounds.min, bounds.max);
    control.input.value = clamp(bounds.value, bounds.min, bounds.max);
    control.input.setAttribute(
      "aria-valuetext",
      getScrollbarPositionText(scrollBar, control.target === "end" ? scrollBar.end : scrollBar.start)
    );
  });
};

// Maps keyboard/native input changes back into the amCharts scrollbar range.
const setRangeFromNativeControl = (scrollBar, target, value) => {
  if (target !== "start" && target !== "end") {
    return;
  }

  const minRange = getScrollbarMinRange(scrollBar);
  const normalizedValue = clamp(value / SCROLLBAR_NATIVE_RANGE_SCALE, 0, 1);

  if (target === "start") {
    setScrollbarRange(scrollBar, clamp(normalizedValue, 0, scrollBar.end - minRange), scrollBar.end, "start");
    return;
  }

  setScrollbarRange(scrollBar, scrollBar.start, clamp(normalizedValue, scrollBar.start + minRange, 1), "end");
};

// Keeps the native input invisible while preserving keyboard and JAWS support.
const styleNativeScrollbarInput = input => {
  Object.assign(input.style, {
    appearance: "none",
    background: "transparent",
    border: "0",
    cursor: "ew-resize",
    margin: "0",
    opacity: "0",
    padding: "0",
    pointerEvents: "none",
    position: "absolute",
    zIndex: "6"
  });
};

// Draws a visible focus frame because the native input itself is transparent.
const styleNativeScrollbarFocusFrame = focusFrame => {
  Object.assign(focusFrame.style, {
    background: "rgba(125, 138, 255, 0.35)",
    border: "1px solid rgba(80, 94, 217, 0.55)",
    borderRadius: "0",
    boxSizing: "border-box",
    display: "none",
    pointerEvents: "none",
    position: "absolute",
    zIndex: "5"
  });
};

// Creates one native range input for either the left/start or right/end grip.
const createNativeScrollbarControl = (scrollBar, target, label) => {
  const input = document.createElement("input");
  const focusFrame = document.createElement("div");

  input.type = "range";
  input.step = "1";
  input.className = "chart-scrollbar-native-range-control";
  input.setAttribute("aria-label", label);
  styleNativeScrollbarInput(input);
  styleNativeScrollbarFocusFrame(focusFrame);

  const handleInput = () => setRangeFromNativeControl(scrollBar, target, Number(input.value));
  const handleFocus = () => {
    focusFrame.style.display = "block";
    positionNativeScrollbarControls(scrollBar);
  };
  const handleBlur = () => { focusFrame.style.display = "none"; };

  input.addEventListener("input", handleInput);
  input.addEventListener("change", handleInput);
  input.addEventListener("focus", handleFocus);
  input.addEventListener("blur", handleBlur);

  return {
    focusFrame,
    input,
    target,
    dispose: () => {
      input.removeEventListener("input", handleInput);
      input.removeEventListener("change", handleInput);
      input.removeEventListener("focus", handleFocus);
      input.removeEventListener("blur", handleBlur);
      input.parentNode?.removeChild(input);
      focusFrame.parentNode?.removeChild(focusFrame);
    }
  };
};

// Batches DOM positioning with animation frames to avoid layout thrash.
const scheduleNativeScrollbarControlPosition = scrollBar => {
  if (typeof window === "undefined") {
    positionNativeScrollbarControls(scrollBar);
    return;
  }

  if (scrollBar._nativeScrollbarPositionFrame) {
    window.cancelAnimationFrame(scrollBar._nativeScrollbarPositionFrame);
  }

  scrollBar._nativeScrollbarPositionFrame = window.requestAnimationFrame(() => {
    scrollBar._nativeScrollbarPositionFrame = undefined;
    syncNativeScrollbarControls(scrollBar);
    positionNativeScrollbarControls(scrollBar);
  });
};

// Installs the two native grip controls and keeps them synced with amCharts events.
const addScrollbarNativeRangeControls = scrollBar => {
  if (typeof document === "undefined" || scrollBar._nativeScrollbarControls) {
    return undefined;
  }

  const container = getNativeControlContainer(scrollBar);
  if (!container) {
    return undefined;
  }

  if (window.getComputedStyle?.(container).position === "static") {
    container.style.position = "relative";
  }

  const controls = [
    createNativeScrollbarControl(scrollBar, "start", getScrollbarControlLabel(scrollBar, "start")),
    createNativeScrollbarControl(scrollBar, "end", getScrollbarControlLabel(scrollBar, "end"))
  ];
  addNativeScrollbarControlElements(container, controls, scrollBar._nativeScrollbarControlsBeforeChart);
  scrollBar._nativeScrollbarControlContainer = container;
  scrollBar._nativeScrollbarControls = controls;

  const schedulePosition = () => scheduleNativeScrollbarControlPosition(scrollBar);
  const disposers = [
    scrollBar.events?.on?.("rangechanged", schedulePosition),
    scrollBar.events?.on?.("sizechanged", schedulePosition),
    scrollBar.events?.on?.("positionchanged", schedulePosition),
    scrollBar.startGrip?.events?.on?.("positionchanged", schedulePosition),
    scrollBar.endGrip?.events?.on?.("positionchanged", schedulePosition)
  ];
  if (typeof window !== "undefined") {
    window.addEventListener("resize", schedulePosition);
  }

  syncNativeScrollbarControls(scrollBar);
  scheduleNativeScrollbarControlPosition(scrollBar);

  return {
    dispose: () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("resize", schedulePosition);
        if (scrollBar._nativeScrollbarPositionFrame) {
          window.cancelAnimationFrame(scrollBar._nativeScrollbarPositionFrame);
        }
      }
      disposers.forEach(disposer => disposer?.dispose?.());
      controls.forEach(control => control.dispose());
      scrollBar._nativeScrollbarControls = undefined;
      scrollBar._nativeScrollbarControlContainer = undefined;
      scrollBar._nativeScrollbarPositionFrame = undefined;
    }
  };
};

// Public helper used by scrollbarX charts to add keyboard/JAWS grip access.
export const enableScrollbarGripKeyboard = (scrollBar, options = {}) => {
  const nextInsertBeforeChart =
    options.insertBeforeChart ?? scrollBar?._nativeScrollbarControlsBeforeChart ?? false;
  const shouldRecreateControls = scrollBar && (
    (options.container && scrollBar._nativeScrollbarControlContainerOverride !== options.container) ||
    (scrollBar._nativeScrollbarControls && scrollBar._nativeScrollbarControlsBeforeChart !== nextInsertBeforeChart)
  );

  if (shouldRecreateControls) {
    scrollBar._nativeRangeControlDisposer?.dispose?.();
    scrollBar._nativeRangeControlDisposer = undefined;
    scrollBar._gripKeyboardHandlersAttached = false;
  }

  if (scrollBar) {
    if (options.container) {
      scrollBar._nativeScrollbarControlContainerOverride = options.container;
    }
    scrollBar._nativeScrollbarLabelPrefix = options.labelPrefix ?? scrollBar._nativeScrollbarLabelPrefix;
    scrollBar._nativeScrollbarStartLabel = options.startLabel ?? scrollBar._nativeScrollbarStartLabel;
    scrollBar._nativeScrollbarEndLabel = options.endLabel ?? scrollBar._nativeScrollbarEndLabel;
    scrollBar._nativeScrollbarControlsBeforeChart = nextInsertBeforeChart;
    setScrollbarControlLabels(scrollBar);
  }

  if (!scrollBar || scrollBar._nativeScrollbarControls) {
    return scrollBar;
  }

  hideScrollbarSpriteFromAssistiveTech(scrollBar);
  hideScrollbarSpriteFromAssistiveTech(scrollBar.startGrip);
  hideScrollbarSpriteFromAssistiveTech(scrollBar.endGrip);
  hideScrollbarSpriteFromAssistiveTech(scrollBar.thumb);
  const nativeRangeControlDisposer = addScrollbarNativeRangeControls(scrollBar);
  if (!nativeRangeControlDisposer) {
    scrollBar._gripKeyboardHandlersAttached = false;
    return scrollBar;
  }

  scrollBar._nativeRangeControlDisposer = nativeRangeControlDisposer;
  scrollBar._gripKeyboardHandlersAttached = true;
  if (!scrollBar._gripKeyboardCleanupDisposer) {
    scrollBar._gripKeyboardCleanupDisposer = scrollBar.events?.on?.("disposed", () => {
      scrollBar._nativeRangeControlDisposer?.dispose?.();
      scrollBar._gripKeyboardCleanupDisposer?.dispose?.();
      scrollBar._gripKeyboardCleanupDisposer = undefined;
      scrollBar._nativeScrollbarControlContainerOverride = undefined;
      scrollBar._nativeScrollbarControlsBeforeChart = false;
      scrollBar._nativeScrollbarLabelPrefix = undefined;
      scrollBar._nativeScrollbarStartLabel = undefined;
      scrollBar._nativeScrollbarEndLabel = undefined;
      scrollBar._gripKeyboardHandlersAttached = false;
    });
  }

  return scrollBar;
};


const excludeFields = (item, fileName, chartType) => {
  //  fields not to include in export CSV
  let fieldsToExclude = [
    'tooltipData',
    'date',
    'entry_ts',
    'display_cellular_usage',
    'anomaly',
    'std',
    'fromDateFormated',
    'toDateFormated',
    'color',
    'hasBulletColumnChartData',
    'sla'
  ];
  // fields that should be included but without formatting
  const fieldsToNotFormat = [
    'application',
    'vqoe_score',
    'client_count',
    'Good',
    'Fair',
    'Poor',
    'anomalous_circuits_cnt',
    'anomalous_circuits_total_cnt',
    'circuit_name',
    'downtimeInMin',
    'totalNumUsers',
    'message',
    'interface',
    'name'
  ];
  const isQoEApplicationsFile = fileName.toLowerCase().includes('qoe_applications');
  const isBandwidthFile = fileName.toLowerCase().includes('bandwidth');
  // Optimize repeated fileName checks in App360 QoE charts and AppMetrics charts
  const fileNameFieldMap = {
    "AppMetrics_Latency": ['jitter', 'loss_percentage', 'vqoe_score'],
    "AppMetrics_Jitter": ['latency', 'loss_percentage', 'vqoe_score'],
    "AppMetrics_PacketLoss": ['latency', 'jitter', 'vqoe_score'],
    "App360_QoE_At_Site": ['latency', 'jitter', 'loss_percentage']
  };
  if (fileNameFieldMap[fileName]) {
    fieldsToExclude = [...fieldsToExclude, ...fileNameFieldMap[fileName]];
  }
  
   return Object.fromEntries(
    Object.entries(item)
      .filter(([key]) => 
        !fieldsToExclude.includes(key) && 
        !key.toLowerCase().includes('tip') && 
        !key.toLowerCase().includes('_anomaly_flag')  //  exclude any key containing "tip"
      )
      .map(([key, value]) => {
        if (fieldsToNotFormat.includes(key) || isQoEApplicationsFile) {
          return [key, value];  
        } 
        if (['local_exit_time', 'fromDate', 'toDate'].includes(key)) {
         return [key, shortDisplayDateTimeHour(value)];
        }
        if (['Anomalous Circuits', 'Loss', 'Anomaly Rate', 'loss_percentage'].includes(key)) {
          return [key, value === '-' ? '0 %' : `${value} %`];
        }
         if (['Jitter', 'Latency', 'jitter', 'latency'].includes(key)) {
          return [key, value === '-' ? '0 ms' : `${value} ms`];
        }
         //Rename recommendationQuality and defaultQuality
        if (key === 'recommendationQuality') {
          return ['RecommendedPathQuality', value === '-' ? '0 %' : `${value} %`];
        }
        if (key === 'defaultQuality') {
          return ['CurrentPathQuality', value === '-' ? '0 %' : `${value} %`];
        }
         // handling for Baseline in AD charts
        if (key === 'Baseline') {
          if (['jitter', 'latency'].includes(fileName.toLowerCase())) {
            return [key, value === '-' ? '0 ms' : `${value} ms`];
          }
          if (fileName.toLowerCase() === 'loss') {
            return [key, value === '-' ? '0 %' : `${value} %`];
          }
          return [key, value]; // fallback if not matched
        }        
         // bandwidth widget   
        if (isBandwidthFile) {
          if (['Tx utilization (%)', 'Rx utilization (%)', 'Utilization'].includes(chartType)) {
            return [key, `${value} %`];
          }
          return [key, formatBitsSI(value)];
        }
        // Add formatting logic here if needed (e.g., %, ms, default values)
        return [key, formatBytes(value)]; // default formatting
      })
  );
};


// This function exports chart data to CSV format(only for am4charts).
// It takes a chart object and a file name as parameters.
// this function used to export csv for Path Analytics tab 
export const exportCSVMultiline = (chart, fileName, exportCSVFields ='NA') => {
  let exportData = [...chart.data];
  // Remove any keys containing '_baseline' from headers and rows
  if (exportData.length === 0) return;
  // Fields to exclude from export
  const excludeFields = [
    'artcxp_avg_cnd',
    'artcxp_avg_snd',
    'artcxp_avg_total',
    'cxpapp_avg_latency',
    'cxpapp_avg_loss',
    'msft_avg_latency',
    'msft_public_ip', 
    'status',
    'entry_time',
    'color',
    'id',
    'index',
    'hasGanttChartData',
    'label',
    'lengthOfPaths',
    'currentPathsColor',
    'currentPathsColors',
    'fromDateFormated',
    'toDateFormated',
    'recommendedPaths'
  ];
  let keys = Object.keys(exportData[0])
    .filter(k => !k.toLowerCase().includes('_baseline'))
    .filter(k => !excludeFields.includes(k));
  // update headers
  let headers = keys.map((key) => {
    if (key === 'date') return 'dateTime';
    if (key.startsWith('value')) {
      const idx = parseInt(key.replace('value', ''), 10);
      return exportCSVFields && exportCSVFields[idx] && exportCSVFields[idx].label ? exportCSVFields[idx].label : key;
    }
    return key;
  });

  // Helper to quote and escape CSV values only when needed
  const quote = (v) => {
    if (v == null) return '';
    const s = String(v);
    if (s.includes('"')) { 
      return '"' + s.replace(/"/g, '""') + '"';
    }
    if (s.includes(',') || s.includes('\n') || s.includes('\r')) {
      return '"' + s + '"';
    }
    return s;
  };
  const delimiter = ",";
  const csvBody = [headers.map(quote).join(delimiter)].concat(
    exportData.map(row => keys.map(k => {
      if (k === 'date' || k === 'fromDate' || k === 'toDate') return quote(shortDisplayDateTimeHour(row[k]));
      let v = row[k];
      if (v == null) return quote(v);
      // Format based on fileName
      const fileNameCSV = fileName.toLowerCase();
      if (fileNameCSV.includes('usage')) { 
        const num = Number(v);
        if (!isNaN(num)) return quote(formatBytes(num));
      }
      if (fileNameCSV.includes('jitter') || fileNameCSV.includes('latency')) {
        if (typeof v === 'number' || (!isNaN(Number(v)) && typeof v !== 'object')) {
          return quote(v + ' ms');
        }
      }
      if (fileNameCSV.includes('loss')) {
        // Only append % if not already present and value is numeric
        if (typeof v === 'number' || (!isNaN(Number(v)) && typeof v !== 'object')) {
          return quote(v + ' %');
        }
      }
        // Additional formatting for specific fileNames
        if (fileNameCSV.includes('resolution_height')) {
          if (typeof v === 'number' || (!isNaN(Number(v)) && typeof v !== 'object')) {
            return quote(v + ' ppi');
          }
        }
        if (fileNameCSV.includes('frame_rate')) {
          if (typeof v === 'number' || (!isNaN(Number(v)) && typeof v !== 'object')) {
            return quote(v + ' fps');
          }
        }
        if (fileNameCSV.includes('bit_rate')) {
          if (typeof v === 'number' || (!isNaN(Number(v)) && typeof v !== 'object')) {
            return quote(v + ' bits');
          }
        }
      return quote(v);
    }).join(delimiter))
  ).join("\r\n");
  downloadAsCSVFile(csvBody, fileName);
};



// This function exports chart data to CSV format(only for am4charts).
// It takes a chart object and a file name as parameters.
export const exportCSV = (chart, fileName, chartType ='NA') => {
  let exportData = [...chart.data];

    // Helper to set export file name with timestamp
    const updateExportFileName = () => { 
      const fileNameTimestamp = fileName +"_" + displayDateTime(new Date(), "yyyy-MM-dd hh mm ss aa");
      chart.exporting.filePrefix = fileNameTimestamp;
    };
    updateExportFileName()
    // Keyboard handler
    chart.exporting.container.addEventListener("keydown", function(e) {
      if (["Enter", " ", "Spacebar"].includes(e.key)) {
      e.preventDefault();
      updateExportFileName();
      }
    });
    // Mouse click or touch (hit) handler
    chart.exporting.events.on("hit", updateExportFileName); 
    chart.exporting.dataSource = exportData;  
    // zoomed data export
    chart.exporting.adapter.add("data", (data) => { 
    let min = chart.xAxes.getIndex(0).minZoomed;
    let max = chart.xAxes.getIndex(0).maxZoomed; 
    // if no zoom, fall back to everything
    if (min == null || max == null) {
    return data;
    } 
    let visible = chart.data
    .filter(item => {
      const ts = item.date || item.entry_ts;
      return ts >= min && ts <= max;
    })
    .map(item => {
      const ts = item.date || item.entry_ts; 
      return {        
        dateTime: shortDisplayDateTimeHour(ts),
        ...excludeFields(item, fileName, chartType),
      };
    });
    return { data: visible };
   });
   
    // Export to CSV
   chart.exporting.export("csv", { download: true }); 
};

export const getCustomValueAxisConfig = (value, toValue, fillColor) => {
  return [
    {
      maximum: 10,
      guides: [
        {
          value,
          toValue,
          lineAlpha: 1,
          lineColor: colors.gray90,
          dashLength: 3,
          fillAlpha: 0.2,
          fillColor,
          inside: true
        }
      ]
    }
  ];
};

/**
 * Description: Converts y axis value into decimal byte unit text
 * reference: https://www.iec.ch/prefixes-binary-multiples
 * @param {number} - y axis value - number of bytes
 * @returns {string} y axis value in decimal byte unit
 */

export const formatYAxisDecimalBytes = value => {
  let result = "";
  if (value <= 0) result = "0";
  else {
    if (value < 1e9) {
      if (value < 1e3) result = value + byteDecimalUnits[0];
      else if (value < 1e6) result = value / 1e3 + byteDecimalUnits[1];
      else result = value / 1e6 + byteDecimalUnits[2];
    }
    else {
      if (value < 1e12) result = value / 1e9 + byteDecimalUnits[3];
      else if (value < 1e15) result = value / 1e12 + byteDecimalUnits[4];
      else if (value < 1e18) result = value / 1e15 + byteDecimalUnits[5];
      else if (value < 1e21) result = value / 1e18 + byteDecimalUnits[6];
      else if (value < 1e24) result = value / 1e21 + byteDecimalUnits[7];
      else result = value / 1e24 + byteDecimalUnits[8];
    }
  }
  return result;
};

export const selectedBWValueAxesConfig = [
  {
    autoGridCount: true,
    gridAlpha: 0.3,
    labelFunction: formatSizeUnits
  }
];

export const chartScrollbarValueAxesConfig = [
  { autoGridCount: true, gridAlpha: 0.3 }
];
export const chartUsageScrollbarValueAxesConfig = [
  {
    autoGridCount: true,
    gridAlpha: 0.3,
    //logarithmic: true,
    labelFunction: function (value, valueText) {
      valueText = value <= 0 ? "0" : formatBytes(value);
      return valueText;
    }
  }
];
// if need chart zoom update enable with true
export const chartScrollbarConfig = {
  enabled: false,
  backgroundColor: "transparent",
  selectedBackgroundColor: "transparent",
  dragIconHeight: 85,
  dragIcon: `${window.location.protocol}//${window.location.host}${process.env.PUBLIC_URL}/ciscoDragIcon`,
  offset: -83,
  scrollbarHeight: 85
};

export const chartScrollbarListeners = [
  {
    event: "init",
    method: e => {
      e.chart.chartDiv.addEventListener("click", () => { });
    }
  },
  {
    event: "rendered",
    method: e => {
      const sb = e.chart.chartScrollbar.set.node;
      sb.addEventListener("mousedown", () => {
        e.chart.mouseIsDown = true;
      });
      e.chart.chartDiv.addEventListener("mouseup", () => {
        e.chart.mouseIsDown = false;
      });
    }
  },
  {
    event: "zoomed",
    method: e => {
      e.chart.lastZoomed = e;
    }
  }
];

/**
 * Description: This method sets style to am4 chart tooltip
 * @param {object} tooltip - tooltip object.
 * @param {boolean} bforecastFlag - whether this is a forecast chart tooltip
 * @returns {void}
 */

export const customizeTooltip = (tooltip, bforecastFlag = false) => {
  // Disable default tooltip style
  tooltip.getFillFromObject = false;
  
  // Set padding
  tooltip.label.paddingTop = 12;
  tooltip.label.paddingRight = 16;
  tooltip.label.paddingBottom = 12;
  tooltip.label.paddingLeft = 16;
  
  // Set tooltip border style
  tooltip.background.strokeWidth = 1;
  tooltip.background.cornerRadius = 6;
  
  // Set tooltip shadow style
  const shadow = tooltip.background.filters.getIndex(0);
  shadow.dy = 4;
  shadow.blur = 12;
  shadow.color = am4core.color(colors.gray0);

  // Apply initial theme colors using centralized utility
  applyTooltipTheme(tooltip, am4core);

  // Listen for theme changes and update tooltip colors dynamically
  const themeObserver = createThemeChangeObserver(() => applyTooltipTheme(tooltip, am4core));

  // Cleanup observer when tooltip is disposed
  tooltip.events.on('disposed', () => themeObserver.disconnect());

  if (bforecastFlag) {
    tooltip.dy = -70;
    // set tooltip orientation horizontal
    tooltip.pointerOrientation = "horizontal";
  } else {
    // set tooltip orientation vertical
    tooltip.pointerOrientation = "vertical";

    // remove tooltip pointer
    tooltip.background.pointerLength = 0;
  }
};

/**
 * Description: This method sets style to am4 chart scroll bar
 * and adds the scroll bar to the given am4 chart
 * @param {object} [chart=null] - am4 chart
 * @param {boolean} [scrollBarSeries=false] - enables series in scroll bar
 * @param {object} [props=null] - enables initial zoom of chart
 * @returns {void}
 */

export const addScrollBar = (
  chart = null,
  scrollBarSeries = false,
  props = null,
  scrollbarOptions = null
) => {
  // create a scroll bar
  const scrollBar = new am4charts.XYChartScrollbar();
  // enable scroll bar
  scrollBar.disabled = false;
  // set scroll bar style
  scrollBar.minHeight = 30;
  scrollBar.thumb.minWidth = 50;
  scrollBar.background.fill = am4core.color(colors.blue70);
  scrollBar.background.fillOpacity = 0.5;
  // enable and customize scroll bar grips
  scrollBar.startGrip.visible = true;
  scrollBar.endGrip.visible = true;
  scrollBar.startGrip.icon.disabled = true;
  scrollBar.startGrip.background.disabled = true;
  const startLine = scrollBar.startGrip.createChild(am4core.Rectangle);
  startLine.height = 30;
  startLine.width = 5;
  startLine.fillOpacity = 0.4;
  startLine.align = "center";
  startLine.valign = "middle";
  scrollBar.endGrip.icon.disabled = true;
  scrollBar.endGrip.background.disabled = true;
  const endLine = scrollBar.endGrip.createChild(am4core.Rectangle);
  endLine.height = 30;
  endLine.width = 5;
  endLine.fillOpacity = 0.4;
  endLine.align = "center";
  endLine.valign = "middle";

  if (chart) {
    if (scrollBarSeries === true) {
      // add graph line reference to scroll bar
      chart.series.each(series => scrollBar.series.push(series));
      // set graph line style in scroll bar
      scrollBar.scrollbarChart.series.each(
        scrollSeries => (scrollSeries.strokeDasharray = "2,4")
      );
    }
    // set scroll bar to x axis
    chart.scrollbarX = scrollBar;
    // set scroll bar parent
    chart.scrollbarX.parent = chart.bottomAxesContainer;
    if (scrollbarOptions) {
      enableScrollbarGripKeyboard(chart.scrollbarX, scrollbarOptions);
    }

    // set initial zoom in x axis
    if (props?.scrollBarInitialZoom) {
      // set start and end of initial zoom in x axis
      let maxDate =
        props.data.length > 0
          ? props.data[props.data.length - 1].date
          : props.max;
      if (maxDate instanceof Date) maxDate = maxDate.getTime();
      if (maxDate > props.max) maxDate = props.max;
      const startDate = new Date(maxDate);
      const hours = Math.round(Math.abs(props.max - props.min) / 36e5) / 3;
      startDate.setHours(startDate.getHours() - hours);
      // zoom in when chart is ready
      chart.events.on("ready", () =>
        chart.xAxes.getIndex(0).zoomToDates(startDate, new Date(maxDate))
      );
      // disable zoom out button
      chart.zoomOutButton.disabled = true;
    }
  }
};

/**
 * Description: This method sets style to legend
 * @param {object} legend - legend object.
 * @param {boolean} [isInteractive=true] - enable / disable interactivity of legends
 * @param {boolean} [isSquare=true] - sets square / bar legends
 * @param {boolean} [isReverse=false] - reverses the order of legend items
 * @returns {void}
 */

export const customizeLegend = (
  legend,
  isInteractive = true,
  isSquare = true,
  isReverse = false
) => {
  // set legend style
  legend.userClassName = "hbr-type-body2";
  legend.contentAlign = "right";
  if (isInteractive === true) {
    legend.position = "top";
    legend.paddingBottom = 8;
  } else {
    legend.position = "bottom";
    legend.itemContainers.template.clickable = false;
    legend.itemContainers.template.focusable = false;
    legend.itemContainers.template.cursorOverStyle =
      am4core.MouseCursorStyle.default;
  }
  // set custom legend icon style
  legend.useDefaultMarker = false;
  legend.markers.template.width = 20;
  legend.markers.template.height = 20;
  const marker = legend.markers.template.children.getIndex(0);
  marker.cornerRadius(0, 0, 0, 0);
  // reverse legend items order for stacked chart
  if (isSquare === true) {
    marker.strokeWidth = 0;
    marker.width = 16;
    marker.height = 16;
    marker.horizontalCenter = "left";
  } else {
    marker.width = 20;
    marker.height = 3;
    marker.valign = "middle";
  }
  if (isReverse === true) legend.reverseOrder = true;
  legend.itemContainers.template.paddingRight = 7;
  
  // Apply theme-aware colors to legend with dynamic theme change observer
  applyLegendTheme(legend, am4core);
};

export const customizeAnomalyLegend = (legend, color) => {
  legend.itemContainers.template.clickable = false;
  legend.itemContainers.template.focusable = false;
  legend.itemContainers.template.cursorOverStyle =
      am4core.MouseCursorStyle.default;
  var marker = legend.markers.template;
  marker.disposeChildren ();
  marker.width = 0;
  marker.height = 0;
  legend.contentAlign = "right";
  legend.userClassName = "hbr-type-body2"
  legend.dx = -245;
  legend.dy = -270;
  let triangle = marker.createChild(am4core.Triangle);
  triangle.width = 10;
  triangle.height = 10;
  triangle.rotation = 180;
  triangle.valign = "middle";
  triangle.dy = -6;
  triangle.fill = color
}

/**
 * Description: This method gives tooltip html string with static values
 * @param {object} series - am4 chart series object.
 * @param {object} data - tooltip data
 * @param {string} [date=""] - tooltip date
 * @param {string} [title=""] - tooltip title
 * @returns {string} - tooltip html string
 */

export const getTooltipHTMLString = (series, data, date = "", title = "") => {
  let tooltipHTML = "";
  if (title || date)
    tooltipHTML += `
      <div class="ttip-header">
        ${title ? `${title}&nbsp;` : ""}
        <span class="date">${date}</span>
      </div>
    `;
  tooltipHTML += `
    <table class="ttip-content">
    ${series
      .map(item => {
        if (!item.isHidden) {
          const legendColor = item.stroke.hex === colors.gray100.toLowerCase()
            ? item.fill.hex : item.stroke.hex;
          const value = data.tooltipData === undefined
            ? data[item.dataFields.valueY]
            : data.tooltipData[item.dataFields.valueY];
          const legendClass =
            item.stacked === true ? "square-legend" : "bar-legend";
          return `
            <tr>
              <td>
                <div class="flex-items">
                  <span
                    class=${legendClass}
                    style="background-color:${legendColor};"
                  ></span>
                  ${item.name || data.title || ""}
                </div>
              </td>
              <td>${value}</td>
            </tr>
          `;
        }
        return "";
      })
      .join("")}
    </table>
  `;
  return tooltipHTML;
};

/**
 * Description: This method gives approximate min height of gantt chart
 * @param {object | any[] | number} data - data to get number of columns
 * @returns {number} - min height in pixels
 */

export const getGanttChartHeight = data => {
  const minHeight = 150;
  let length = 0;
  if (Number.isInteger(data)) length = data;
  else if (Array.isArray(data)) length = data.length;
  else if (data instanceof Object) length = Object.entries(data).length;
  return length > 2.5 ? (length * 22) + 100 : minHeight;
};

export const getTooltipDate = (dataIdx, item, timestamp, tooltipTimestamp) => {
  if ((dataIdx === item.data.length - 2 || dataIdx === item.data.length - 1)) {
    if (dataIdx === item.data.length - 2) {
      if (tooltipTimestamp > timestamp && tooltipTimestamp <= timestamp + 900000) {
        return true
      }
    } else {
      if ((tooltipTimestamp > timestamp || tooltipTimestamp + 900000 > timestamp) &&
        tooltipTimestamp <= timestamp + 900000) {
        return true
      }
    }
  } else if (dataIdx !== item.data.length - 2 && dataIdx !== item.data.length - 1 &&
    timestamp % 100000 === 0 && tooltipTimestamp >= timestamp &&
    tooltipTimestamp < startOfHour(timestamp).valueOf() + 3600000) {
    return true
  }
  return false
}

export const getTooltipDateStacked = (dataIdx, item, timestamp, tooltipTimestamp) => {
  if (((dataIdx === item.data.length - 2 || dataIdx === item.data.length - 1) &&
    tooltipTimestamp < timestamp || tooltipTimestamp > timestamp + 900000) ||
    (dataIdx !== item.data.length - 2 && dataIdx !== item.data.length - 1 && tooltipTimestamp < timestamp ||
      tooltipTimestamp > timestamp + 3599999)) {
    return true
  }
  return false
}

export const getTimeState = (isSidebar = false) => {
  const { globalFilter } = store.getState().vanalytics.app;
  const v4Payload = isSidebar === false
    ? globalFilter.globalV4Payload
    : globalFilter.globalV4PayloadSidebar;
  return {
    interval: getTimeInterval(v4Payload.time_frame),
    min: v4Payload.entry_ts.start,
    max: v4Payload.entry_ts.end
  };
};

 /* function resolves CSS variable colors to their actual values, 
    ensuring compatibility with amCharts and dynamic theming.
    Page : Overview; Widget : Applications;  Field : Distribution by QOE(am4charts)  
 */
export const getCssVariableColor = (color) => {
    if (typeof color === "string" && color.startsWith("var(")) {
      // Extract variable name and fallback
      const match = color.match(/var\((--[\w-]+),?\s*([^)]+)?\)/);
      if (match) {
        const cssVar = match[1];
        const fallback = match[2] || "";
        // Try to get value from body, then root
        let value = getComputedStyle(document.body).getPropertyValue(cssVar).trim();
        if (!value) {
        value = getComputedStyle(document.documentElement).getPropertyValue(cssVar).trim();
        }
        return value || fallback;
      }
    }
    return color;
};
